import { useState, useEffect } from "react";
import { DayPickerRangeController } from "react-dates";
import { useIntl } from "react-intl";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import PropTypes from "prop-types";
import moment from "moment";
// TODO: we need to analyze how to implement internationalization with the momentary library
import "moment/locale/es";
import { Box } from "@material-ui/core";
import { DateRange as DateRangeIcon } from "@material-ui/icons";
import "react-dates/lib/css/_datepicker.css";
import "react-dates/initialize";
import CalendarFooter, { displayDate } from "./CalendarFooter";
import useStyles from "./dateRangeStyles";
import SrFilterButton from "../SrFilterButton/SrFilterButton";
import translate from "../../../i18n/translate";
import { SrDialog, SrButton } from "..";

const START_DATE_INPUT_ID = "startDate";

const inputDateValue = (startDate, endDate, monthSelector) => {
  if (monthSelector && startDate.isSame(moment(), "month")) {
    return translate("common.dates.thisMonth");
  }
  if (startDate.isSame(endDate, "day") && startDate.isSame(moment(), "day")) {
    return translate("common.dates.today");
  }

  return displayDate(startDate, endDate);
};

const DateRange = ({
  onDateSelected,
  monthSelector,
  minDate,
  actionButtons,
}) => {
  const classes = useStyles();
  const [selectedStartDate, setSelectedStartDate] = useState(
    monthSelector ? moment().startOf("month") : moment()
  );
  const [selectedEndDate, setSelectedEndDate] = useState(moment());
  const [currentStartDate, setCurrentStartDate] = useState(
    monthSelector ? moment().startOf("month") : moment()
  );
  const [currentEndDate, setCurrentEndDate] = useState(moment());

  const [focussedInput, setFocusedInput] = useState(null);

  useEffect(() => {
    setCurrentStartDate(selectedStartDate);
    setCurrentEndDate(selectedEndDate);
  }, [selectedEndDate, selectedStartDate]);

  const intl = useIntl();

  const theme = useTheme();
  const matchesDownSm = useMediaQuery(theme.breakpoints.down("sm"));
  const matchesDownXs = useMediaQuery(theme.breakpoints.down("xs"));

  const onDatesPick = ({ startDate, endDate }) => {
    if (monthSelector) {
      const endOfMonth = startDate.isSame(moment(), "month")
        ? moment()
        : startDate.clone().endOf("month");

      setCurrentStartDate(startDate.clone().startOf("month"));
      setCurrentEndDate(endOfMonth);
    } else {
      setCurrentStartDate(startDate);
      setCurrentEndDate(endDate);
    }
  };

  const inputFocusHandler = (focusedInput) => {
    if (monthSelector) {
      setFocusedInput(START_DATE_INPUT_ID);
    } else if (focusedInput) {
      setFocusedInput(focusedInput);
    } else {
      setFocusedInput(START_DATE_INPUT_ID);
    }
  };

  const closeHandler = () => {
    setFocusedInput(null);
    setCurrentStartDate(selectedStartDate);
    setCurrentEndDate(selectedEndDate);
  };

  const dateSelectedHandler = () => {
    setFocusedInput(null);
    setSelectedStartDate(currentStartDate);
    setSelectedEndDate(currentEndDate);
    onDateSelected({
      startDate: currentStartDate,
      endDate: currentEndDate,
    });
  };

  const setDateHandler = (days) => {
    const startDate = moment().subtract(days, "day");
    const endDate = days === 1 ? startDate : moment();

    setFocusedInput(null);
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
    onDateSelected({
      startDate,
      endDate,
    });
  };

  const isOutsideRange = (day) =>
    minDate
      ? day.isAfter(moment(), "day") || day.isBefore(moment(minDate), "day")
      : day.isAfter(moment(), "day");

  return (
    <>
      <SrFilterButton
        onClick={() => setFocusedInput(START_DATE_INPUT_ID)}
        endIcon={<DateRangeIcon />}
      >
        {inputDateValue(selectedStartDate, selectedEndDate, monthSelector)}
      </SrFilterButton>
      <SrDialog
        heading={intl.formatMessage({
          id: "dateRangeSelectorDialogHeading",
        })}
        headerContent={
          actionButtons && (
            <Box display="flex" justifyContent="space-between" paddingTop={1}>
              <SrButton
                onClick={() => setDateHandler(0)}
                className={classes.btnAction}
              >
                {intl.formatMessage({ id: "common.dates.today" })}
              </SrButton>
              <SrButton
                onClick={() => setDateHandler(1)}
                className={classes.btnAction}
              >
                {intl.formatMessage({ id: "common.dates.yesterday" })}
              </SrButton>
              <SrButton
                onClick={() => setDateHandler(7)}
                className={classes.btnAction}
              >
                {intl.formatMessage({ id: "common.dates.sevenDays" })}
              </SrButton>
              <SrButton
                onClick={() => setDateHandler(30)}
                className={classes.btnAction}
              >
                {intl.formatMessage({ id: "common.dates.thirtyDays" })}
              </SrButton>
            </Box>
          )
        }
        coloredHeader
        open={!!focussedInput}
        handleClose={closeHandler}
        size="md"
        fullWidth={matchesDownXs ? true : false}
        dialogContentProps={{
          classes: {
            root: classes.dialogContentRoot,
          },
        }}
      >
        <div
          role="dialog"
          aria-label={intl.formatMessage({
            id: "dateRangeSelector",
          })}
          className={classes.calendarWapper}
        >
          <DayPickerRangeController
            noBorder
            focusedInput={focussedInput}
            numberOfMonths={matchesDownSm ? 1 : 2}
            onDatesChange={onDatesPick}
            onFocusChange={inputFocusHandler}
            startDate={currentStartDate}
            endDate={currentEndDate}
            hideKeyboardShortcutsPanel
            onOutsideClick={closeHandler}
            minimumNights={0}
            initialVisibleMonth={
              matchesDownSm ? undefined : () => moment().subtract(1, "month")
            }
            isOutsideRange={isOutsideRange}
            renderCalendarInfo={() => (
              <CalendarFooter
                startDate={currentStartDate}
                endDate={currentEndDate}
                closeHandler={closeHandler}
                dateSelectedHandler={dateSelectedHandler}
              />
            )}
          />
        </div>
      </SrDialog>
    </>
  );
};

DateRange.propTypes = {
  onDateSelected: PropTypes.func.isRequired,
  monthSelector: PropTypes.bool,
  minDate: PropTypes.string,
  actionButtons: PropTypes.bool,
};

DateRange.defaultProps = {
  monthSelector: false,
  minDate: undefined,
  actionButtons: false,
};

export default DateRange;
